import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * Quick add: in-card panel (server-rendered) or optional true modal.
 */
export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #panelListenersAbort = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {HTMLElement | null} */
  #previousFocus = null;
  /** @type {boolean | null} */
  #slideshowDisabledBeforeOpen = null;
  /** @type {Element | null} */
  #slideshow = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  get #isInCard() {
    return this.dataset.presentation !== 'modal';
  }

  get #isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getPanel() {
    const panel = this.refs.panel;
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #getChooseButton() {
    const button = this.refs.chooseButton;
    return button instanceof HTMLButtonElement ? button : this.querySelector('.quick-add__button--choose');
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#isInCard) {
      this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    } else {
      mediaQueryLarge.addEventListener('change', this.#closeModal);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#panelListenersAbort?.abort();
    mediaQueryLarge.removeEventListener('change', this.#closeModal);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    if (this.#isOpen && this.#isInCard) {
      removeTrapFocus();
      this.#setGalleryInteractive(true);
      this.removeAttribute('open');
      if (!document.querySelector('quick-add-component[open]')) {
        document.body.classList.remove('quick-add-panel-open');
      }
    }
  }

  /**
   * Handles quick add button click
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isInCard) {
      if (this.#isOpen) {
        this.close();
        return;
      }
      this.#openPanel();
      return;
    }

    // Modal path: fetch compact PDP content into the shared dialog
    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (!productGrid) return;

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    const target = document.getElementById('quick-add-modal-content');
    if (!target) return;

    await this.#updateModalContent(freshContent, target);
    this.#openModal();
  };

  /**
   * Public close handler (used by close button via on:click)
   */
  close = () => {
    if (this.#isInCard) {
      this.#closePanel();
    } else {
      this.#closeModal();
    }
  };

  #openPanel() {
    const panel = this.#getPanel();
    if (!panel) return;

    document.querySelectorAll('quick-add-component[open]').forEach((el) => {
      if (el !== this && el instanceof QuickAddComponent) {
        el.close();
      }
    });

    this.#previousFocus = this.#getChooseButton();
    this.setAttribute('open', '');
    this.toggleAttribute('stay-visible', true);

    panel.hidden = false;
    panel.removeAttribute('inert');

    this.#getChooseButton()?.setAttribute('aria-expanded', 'true');
    this.closest('.card-gallery')?.classList.add('card-gallery--quick-add-open');
    this.closest('product-card')?.setAttribute('data-quick-add-open', '');
    document.body.classList.add('quick-add-panel-open');
    this.#setGalleryInteractive(false);

    this.#panelListenersAbort?.abort();
    this.#panelListenersAbort = new AbortController();
    const { signal } = this.#panelListenersAbort;

    document.addEventListener('keydown', this.#handlePanelKeydown, { signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointer, { signal });

    requestAnimationFrame(() => {
      trapFocus(panel);
      const closeButton = this.refs.closeButton;
      if (closeButton instanceof HTMLElement) {
        closeButton.focus();
      }
    });
  }

  #closePanel() {
    const panel = this.#getPanel();
    if (!panel || !this.#isOpen) return;

    this.#panelListenersAbort?.abort();
    this.#panelListenersAbort = null;

    removeTrapFocus();

    panel.hidden = true;
    panel.setAttribute('inert', '');
    this.removeAttribute('open');
    this.toggleAttribute('stay-visible', false);

    this.#getChooseButton()?.setAttribute('aria-expanded', 'false');
    this.closest('.card-gallery')?.classList.remove('card-gallery--quick-add-open');
    this.closest('product-card')?.removeAttribute('data-quick-add-open');
    this.#setGalleryInteractive(true);

    if (!document.querySelector('quick-add-component[open]')) {
      document.body.classList.remove('quick-add-panel-open');
    }

    if (this.#previousFocus instanceof HTMLElement) {
      this.#previousFocus.focus();
    }
    this.#previousFocus = null;
  }

  /**
   * Disable/enable the card gallery slideshow while the in-card panel is open.
   * @param {boolean} enabled
   */
  #setGalleryInteractive(enabled) {
    if (!enabled) {
      this.#slideshow = this.closest('.card-gallery')?.querySelector('slideshow-component') ?? null;
      if (!this.#slideshow) return;
      this.#slideshowDisabledBeforeOpen = this.#slideshow.getAttribute('disabled') === 'true';
      this.#slideshow.setAttribute('disabled', 'true');
      return;
    }

    const slideshow = this.#slideshow;
    this.#slideshow = null;
    if (!slideshow) return;

    if (this.#slideshowDisabledBeforeOpen) {
      slideshow.setAttribute('disabled', 'true');
    } else {
      slideshow.removeAttribute('disabled');
    }
    this.#slideshowDisabledBeforeOpen = null;
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handlePanelKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closePanel();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handleOutsidePointer = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#closePanel();
  };

  /**
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    if (!this.#isOpen) return;
    this.#closePanel();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Builds compact modal content (modal presentation only).
   * @param {Element} productGrid
   * @param {Element} target
   */
  async #updateModalContent(productGrid, target) {
    const productDetails = productGrid.querySelector('.product-details');
    const buyButtons =
      productGrid.querySelector('.buy-buttons-block') || productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');

    if (productDetails && buyButtons && variantPicker && productPrice) {
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice.cloneNode(true));

      variantPicker.querySelectorAll('legend').forEach((legend) => {
        const valueSpan = legend.querySelector('.variant-option__swatch-value');
        const rawText = (valueSpan ? legend.firstChild?.textContent : legend.textContent)?.trim() || '';
        const cleaned = rawText.replace(/^Select\s+/i, '').replace(/\bColor\b/gi, 'Colour');
        if (valueSpan) {
          if (legend.firstChild) legend.firstChild.textContent = cleaned + ' ';
        } else {
          legend.textContent = cleaned;
        }
      });
      variantPicker.querySelectorAll('.custom-variant-close').forEach((el) => el.remove());

      productGrid.replaceChildren(productHeader, variantPicker, buyButtons);
    }

    morph(target, productGrid);
    this.#syncVariantSelection(target);
  }

  /**
   * @param {Element} container
   */
  #syncVariantSelection(container) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = container.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
